// apps/admin/src/features/audit/DiffViewer.tsx
//
// Phase 8.3 Wave 5 Task 39 — Side-by-side before/after diff viewer.
//
// vg-binding: CONTEXT:decisions=P8.D-222
// vg-binding: INTERFACE-STANDARDS:error-shape
//
// Renders the flat key-path diff array emitted by the BE:
//   • each entry is { path: "settings.theme", before, after } pre-flattened
//     to depth ≤ 3 server-side;
//   • arrays of >10 items arrive as { path, collapsed:true, count } stubs
//     that the user expands via the "Xem tất cả" affordance below;
//   • "Xem JSON đầy đủ" toggles a modal-less inline block that pretty-prints
//     the raw before/after values for keys where the per-row inline view
//     is not enough.
//
// The component is intentionally presentation-only — no fetch, no
// state-sync. It accepts the diff array via props so the parent (page +
// drawer) controls when to mount/unmount.

import { useState, type JSX } from 'react';

import type { AuditV2DiffEntry } from '../../api/audit-log-v2.api.js';
import { useTranslation } from '../../i18n/useTranslation.js';

type TFn = (key: string, vars?: Record<string, unknown>) => string;

/** Heuristic value classification for tint + symbol. */
type DiffKind = 'added' | 'removed' | 'modified' | 'unchanged';

function classify(entry: AuditV2DiffEntry): DiffKind {
  const beforeAbsent = entry.before === undefined || entry.before === null;
  const afterAbsent = entry.after === undefined || entry.after === null;
  if (beforeAbsent && !afterAbsent) return 'added';
  if (!beforeAbsent && afterAbsent) return 'removed';
  if (JSON.stringify(entry.before) === JSON.stringify(entry.after)) {
    return 'unchanged';
  }
  return 'modified';
}

function formatValue(value: unknown): string {
  if (value === undefined) return '—';
  if (value === null) return 'null';
  if (typeof value === 'string') {
    // Strip ASCII control chars defensively (II-3 lens-input-injection).
    // React already escapes HTML so we only worry about non-printable bytes
    // that would visibly garble the <pre> output.
    // eslint-disable-next-line no-control-regex
    return value.replace(/[\x00-\x1F\x7F]/g, '').slice(0, 500);
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '[unserializable]';
  }
}

function kindBadge(t: TFn, kind: DiffKind): { label: string; cls: string } {
  switch (kind) {
    case 'added':
      return {
        label: t('audit.diff.kind.added'),
        cls: 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800',
      };
    case 'removed':
      return {
        label: t('audit.diff.kind.removed'),
        cls: 'bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800',
      };
    case 'modified':
      return {
        label: t('audit.diff.kind.modified'),
        cls: 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
      };
    case 'unchanged':
      return {
        label: t('audit.diff.kind.unchanged'),
        cls: 'bg-pw-surface text-pw-text-muted border-pw-border',
      };
  }
}

export interface DiffViewerProps {
  /** Flat diff array straight off the BE row. */
  diff: AuditV2DiffEntry[];
  /** When true the BE truncated the diff array (max 50 entries). */
  truncated?: boolean;
  /** Raw before/after objects for "Xem JSON đầy đủ" affordance. */
  rawBefore?: unknown;
  rawAfter?: unknown;
  /** Allow parent to override the testid namespace. */
  testIdPrefix?: string;
}

/**
 * `<DiffViewer>` — side-by-side flat key-path diff renderer.
 *
 * Renders an empty-state hint when the diff array is empty (e.g. read-only
 * events like AUTH_LOGIN). The component does NOT mutate the diff payload
 * and renders all values defensively through {@link formatValue}.
 */
export function DiffViewer(props: DiffViewerProps): JSX.Element {
  const { t } = useTranslation('ops');
  const { diff, truncated, rawBefore, rawAfter } = props;
  const prefix = props.testIdPrefix ?? 'audit-diff';
  const [expandedArrays, setExpandedArrays] = useState<Set<string>>(
    () => new Set<string>(),
  );
  const [showRaw, setShowRaw] = useState(false);

  function toggleArray(path: string): void {
    setExpandedArrays((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }

  if (diff.length === 0) {
    return (
      <div
        className="rounded-md border border-pw-border bg-pw-surface p-4 text-sm text-pw-text-muted"
        data-testid={`${prefix}-empty`}
      >
        {t('audit.diff.noChanges')}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2" data-testid={`${prefix}-root`}>
      {/* Header strip — column labels */}
      <div className="grid grid-cols-12 items-center gap-2 border-b border-pw-border pb-1 text-[11px] font-medium uppercase tracking-wide text-pw-text-muted">
        <div className="col-span-4">{t('audit.diff.columnField')}</div>
        <div className="col-span-4">{t('audit.diff.columnBefore')}</div>
        <div className="col-span-4">{t('audit.diff.columnAfter')}</div>
      </div>

      {/* Diff rows */}
      <ul className="flex flex-col divide-y divide-pw-border">
        {diff.map((entry) => {
          const kind = classify(entry);
          const badge = kindBadge(t, kind);
          const isCollapsedArray = entry.collapsed === true;
          const expanded = expandedArrays.has(entry.path);
          return (
            <li
              key={entry.path}
              className="grid grid-cols-12 items-start gap-2 py-2 text-sm"
              data-testid={`${prefix}-row-${entry.path}`}
            >
              <div className="col-span-4 flex flex-col gap-1">
                <span
                  className="break-all font-mono text-xs text-pw-text"
                  title={entry.path}
                >
                  {entry.path}
                </span>
                <span
                  className={`inline-flex w-fit items-center rounded-md border px-1.5 py-0.5 text-[10px] font-medium ${badge.cls}`}
                >
                  {badge.label}
                </span>
              </div>
              <div className="col-span-4">
                <pre className="max-h-32 overflow-auto whitespace-pre-wrap break-words rounded-md border border-pw-border bg-pw-surface p-1.5 font-mono text-[11px] leading-snug text-pw-text">
                  {formatValue(entry.before)}
                </pre>
              </div>
              <div className="col-span-4">
                <pre className="max-h-32 overflow-auto whitespace-pre-wrap break-words rounded-md border border-pw-border bg-pw-surface p-1.5 font-mono text-[11px] leading-snug text-pw-text">
                  {formatValue(entry.after)}
                </pre>
                {isCollapsedArray ? (
                  <button
                    type="button"
                    onClick={() => toggleArray(entry.path)}
                    className="mt-1 text-xs font-medium text-pw-primary hover:underline"
                    data-testid={`${prefix}-expand-${entry.path}`}
                  >
                    {expanded
                      ? t('audit.diff.collapse')
                      : t('audit.diff.expandAll', { count: entry.count ?? '?' })}
                  </button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>

      {truncated ? (
        <div
          className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 px-3 py-2 text-xs text-amber-800 dark:text-amber-300"
          data-testid={`${prefix}-truncated-banner`}
        >
          {t('audit.diff.truncated')}
        </div>
      ) : null}

      {/* Raw JSON modal-less reveal — keeps DOM lean when collapsed. */}
      <div className="flex items-center justify-end">
        <button
          type="button"
          onClick={() => setShowRaw((v) => !v)}
          className="text-xs font-medium text-pw-primary hover:underline"
          data-testid={`${prefix}-toggle-raw`}
        >
          {showRaw ? t('audit.diff.hideJson') : t('audit.diff.showJson')}
        </button>
      </div>
      {showRaw ? (
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          <div className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wide text-pw-text-muted">
              {t('audit.diff.rawBefore')}
            </span>
            <pre
              className="max-h-64 overflow-auto rounded-md border border-pw-border bg-pw-bg p-2 font-mono text-[11px] leading-snug text-pw-text"
              data-testid={`${prefix}-raw-before`}
            >
              {formatValue(rawBefore ?? {})}
            </pre>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wide text-pw-text-muted">
              {t('audit.diff.rawAfter')}
            </span>
            <pre
              className="max-h-64 overflow-auto rounded-md border border-pw-border bg-pw-bg p-2 font-mono text-[11px] leading-snug text-pw-text"
              data-testid={`${prefix}-raw-after`}
            >
              {formatValue(rawAfter ?? {})}
            </pre>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default DiffViewer;
